import { useTheme } from '@mui/material';
import Box from '@mui/material/Box';
import Skeleton from '@mui/material/Skeleton';
import { Theme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import {
  Circle,
  GoogleMap,
  Marker,
  Polygon,
  useJsApiLoader,
} from '@react-google-maps/api';
import { WaypointType } from '@treadinc/horizon-api-spec';
import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { getMapStyles } from '~components/Maps/mapStyles';
import { GOOGLE_MAPS_LOADER_OPTIONS } from '~components/Maps/v2/constants';
import { DEFAULT_MAP_CENTER } from '~constants/mapConsts';
import { LocationHistoryMinimal } from '~hooks/useLocationHistory/models';
import { interpolatePositionAtTime } from '~pages/Admin/Geo/Distances/DistanceMap/catmullRomSpline';
import { createWaypointSvgDataUrl } from '~pages/Admin/Geo/mapIcons';

import { buildReplayRoutePath } from './buildReplayRoutePath';
import { getReplayBucketColor } from './replayBuckets';
import ReplayDotOverlay from './ReplayDotOverlay';
import { HighlightedLoad } from './replayInteractions';
import { ReplaySite } from './replaySites';

const MAP_HEIGHT_IN_PX = 300;
// Padding (px) applied when fitting the map bounds to all truck routes.
const FIT_BOUNDS_PADDING_IN_PX = 40;
// Zoom used only as a fallback when there are no points to fit to.
const EMPTY_MAP_ZOOM = 4;

export interface ReplayMapTruck {
  color: string;
  jobId: string;
  label: string;
  pings: LocationHistoryMinimal[];
}

interface TruckReplayMapProps {
  highlightedLoad: HighlightedLoad | null;
  onSelectTruck: (jobId: string) => void;
  replayTimeMs: number;
  selectedJobId: string | null;
  sites: ReplaySite[];
  trucks: ReplayMapTruck[];
}

const MAP_CONTAINER_STYLE = { height: '100%', width: '100%' };
const MAP_OPTIONS: google.maps.MapOptions = {
  disableDefaultUI: true,
  gestureHandling: 'greedy',
  zoomControl: true,
};

// Site markers sit above routes but below the live truck markers (zIndex 1000).
const SITE_MARKER_Z_INDEX = 400;

// Geofence fill/outline color matches the Gantt status buckets so a pickup
// geofence reads the same amber as the "At pickup" bars and a dropoff geofence
// the same green as "At dropoff"; mixed/other sites stay neutral blue.
function geofenceColorForMarkerType(markerType: WaypointType | 'both', theme: Theme) {
  if (markerType === WaypointType.PICKUP) return getReplayBucketColor('AT_PICKUP');
  if (markerType === WaypointType.DROP_OFF) return getReplayBucketColor('AT_DROPOFF');

  return theme.brandV2.colors.treadBlue;
}

function makeMarkerIcon(color: string, bearing: number): google.maps.Symbol {
  return {
    fillColor: color,
    fillOpacity: 1,
    path: window.google.maps.SymbolPath.FORWARD_CLOSED_ARROW,
    rotation: bearing,
    scale: 5,
    strokeColor: '#FFFFFF', // white outline keeps the arrow visible over any route color
    strokeWeight: 1.5,
  };
}

// GeoJSON polygon rings are [[[lng, lat], ...]]; Google Maps wants [{ lat, lng }].
function toPolygonPaths(coordinates: number[][][]) {
  return (coordinates[0] ?? []).map(([lng, lat]) => ({ lat, lng }));
}

const SiteOverlay = ({ site }: { site: ReplaySite }) => {
  const theme = useTheme();
  const { geofence } = site;

  const geofenceColor = geofenceColorForMarkerType(site.markerType, theme);
  const geofenceOptions = {
    clickable: false,
    fillColor: geofenceColor,
    fillOpacity: 0.12,
    strokeColor: geofenceColor,
    strokeOpacity: 0.85,
    strokeWeight: 2,
    zIndex: 1, // beneath routes + markers
  };

  const circle =
    geofence?.type === 'circle' &&
    typeof geofence.circleCenter?.lat === 'number' &&
    typeof geofence.circleCenter?.lon === 'number'
      ? {
          center: { lat: geofence.circleCenter.lat, lng: geofence.circleCenter.lon },
          radius: geofence.circleRadius,
        }
      : null;

  const polygonPaths =
    geofence?.type === 'polygon' && geofence.geojson?.coordinates
      ? toPolygonPaths(geofence.geojson.coordinates as number[][][])
      : [];

  return (
    <Fragment>
      {circle ? (
        <Circle center={circle.center} options={geofenceOptions} radius={circle.radius} />
      ) : null}
      {polygonPaths.length > 1 ? (
        <Polygon options={geofenceOptions} paths={polygonPaths} />
      ) : null}
      <Marker
        icon={{
          url: createWaypointSvgDataUrl(site.markerType),
          anchor: new window.google.maps.Point(12, 12),
          scaledSize: new window.google.maps.Size(24, 24),
        }}
        position={site.position}
        title={site.name}
        zIndex={SITE_MARKER_Z_INDEX}
      />
    </Fragment>
  );
};

const TruckReplayMap = ({
  highlightedLoad,
  onSelectTruck,
  replayTimeMs,
  selectedJobId,
  sites,
  trucks,
}: TruckReplayMapProps) => {
  const theme = useTheme();
  const { isLoaded } = useJsApiLoader(GOOGLE_MAPS_LOADER_OPTIONS);
  const mapRef = useRef<google.maps.Map | null>(null);
  const [mapInstance, setMapInstance] = useState<google.maps.Map | null>(null);

  const timedTrucks = useMemo(
    () =>
      trucks.map((truck) => ({ ...truck, timedPath: buildReplayRoutePath(truck.pings) })),
    [trucks],
  );

  // Isolating a truck narrows the map to just that route.
  const visibleTrucks = useMemo(
    () =>
      selectedJobId
        ? timedTrucks.filter((truck) => truck.jobId === selectedJobId)
        : timedTrucks,
    [selectedJobId, timedTrucks],
  );

  // The emphasized sub-route for a highlighted load (its pings only). Skipped
  // when another truck is isolated, so isolation never leaves a hidden truck's
  // route drawn (the highlighted load must belong to the visible truck).
  const highlightedPath = useMemo(() => {
    if (!highlightedLoad) return null;
    if (selectedJobId && selectedJobId !== highlightedLoad.jobId) return null;

    const truck = trucks.find((candidate) => candidate.jobId === highlightedLoad.jobId);
    if (!truck) return null;

    const { rawPath } = buildReplayRoutePath(
      truck.pings.filter((ping) => ping.loadId === highlightedLoad.loadId),
    );

    return rawPath.length > 0 ? { color: truck.color, points: rawPath } : null;
  }, [highlightedLoad, selectedJobId, trucks]);

  const dotTrucks = useMemo(
    () =>
      visibleTrucks.map((truck) => ({
        color: truck.color,
        jobId: truck.jobId,
        points: truck.timedPath.rawPath,
      })),
    [visibleTrucks],
  );

  const dimmedJobIds = useMemo(
    () =>
      new Set(
        highlightedLoad
          ? visibleTrucks
              .filter((truck) => truck.jobId !== highlightedLoad.jobId)
              .map((truck) => truck.jobId)
          : [],
      ),
    [highlightedLoad, visibleTrucks],
  );

  const allPoints = useMemo(
    () => visibleTrucks.flatMap((truck) => truck.timedPath.rawPath),
    [visibleTrucks],
  );

  const hasAnyPoints = allPoints.length > 0 || sites.length > 0;

  const fitBounds = useCallback(
    (map: google.maps.Map) => {
      const bounds = new window.google.maps.LatLngBounds();
      let extended = false;
      allPoints.forEach((point) => {
        bounds.extend(point);
        extended = true;
      });
      sites.forEach((site) => {
        bounds.extend(site.position);
        extended = true;
      });
      if (!extended) return;

      map.fitBounds(bounds, FIT_BOUNDS_PADDING_IN_PX);
    },
    [allPoints, sites],
  );

  // Re-fit when the visible route set changes (e.g. isolating a single truck).
  useEffect(() => {
    if (mapRef.current) fitBounds(mapRef.current);
  }, [fitBounds]);

  if (!isLoaded) {
    return (
      <Skeleton variant="rectangular" sx={{ height: MAP_HEIGHT_IN_PX, width: '100%' }} />
    );
  }

  return (
    <Box>
      <Box sx={{ height: MAP_HEIGHT_IN_PX, width: '100%' }}>
        <GoogleMap
          center={hasAnyPoints ? undefined : DEFAULT_MAP_CENTER}
          mapContainerStyle={MAP_CONTAINER_STYLE}
          onLoad={(map) => {
            mapRef.current = map;
            setMapInstance(map);
            fitBounds(map);
          }}
          options={{
            ...MAP_OPTIONS,
            styles: getMapStyles(theme.palette.mode === 'dark'),
          }}
          zoom={hasAnyPoints ? undefined : EMPTY_MAP_ZOOM}
        >
          {sites.map((site) => (
            <SiteOverlay key={site.id} site={site} />
          ))}

          {visibleTrucks.map((truck) => {
            const positioned = interpolatePositionAtTime(truck.timedPath, replayTimeMs);
            if (!positioned) return null;

            return (
              <Marker
                key={truck.jobId}
                icon={makeMarkerIcon(truck.color, positioned.bearing)}
                position={positioned.position}
                zIndex={1000}
              />
            );
          })}
        </GoogleMap>
      </Box>

      <ReplayDotOverlay
        dimmedJobIds={dimmedJobIds}
        highlightColor={highlightedPath?.color ?? null}
        highlightedPoints={highlightedPath?.points ?? null}
        map={mapInstance}
        trucks={dotTrucks}
      />

      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, p: 1 }}>
        {trucks.map((truck) => {
          const isDimmed = selectedJobId !== null && selectedJobId !== truck.jobId;

          return (
            <Box
              key={truck.jobId}
              component="button"
              onClick={() => onSelectTruck(truck.jobId)}
              sx={{
                alignItems: 'center',
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                display: 'flex',
                gap: 0.5,
                opacity: isDimmed ? 0.4 : 1,
                p: 0,
              }}
            >
              <Box
                sx={{
                  backgroundColor: truck.color,
                  borderRadius: '50%',
                  height: 10,
                  width: 10,
                }}
              />
              <Typography
                variant="caption"
                sx={{ color: theme.brandV2.colors.treadGray3 }}
              >
                {truck.label}
              </Typography>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};

export default TruckReplayMap;
